import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AssistAnswerChoice,
  IAssistRequestAssigned,
} from '../../../utils/assistRequest/types';
import {
  AssistCardStatus,
  IAssistCard,
  IAssistRequestState,
} from './assistRequest.props';

/**
 * HITL-1770 (HITL 8.0.0): the lobby assist request assigned to this agent.
 *
 * task-router assigns each request straight to several idle agents, with the
 * full request; the card shows at once, with no claim step, and each agent
 * answers on their own (the AI weighs the answers). One card at a time. One
 * click is final: the answer ends the card, so the agent is free for the next
 * one. No answer is fine too: the card ends at its local deadline and nothing
 * is sent (who answered is in the data). This slice holds no cart and no
 * control state: answering an assist request is never a takeover, so nothing
 * here may be read by the session console.
 *
 * Nothing comes back for a card once it shows: the answer goes out over REST
 * (`/v1/event`) and the card ends on the click, its deadline, or an order.
 */

const MAX_ENDED_IDS = 100;
/**
 * Subtracted from `remaining_ms` at receipt, so the card closes before the
 * server's deadline rather than after it (contract §4.4).
 */
export const ASSIST_NETWORK_MARGIN_MS = 300;
/**
 * Upper bound on the `remaining_ms` a card trusts. Counting from receipt avoids
 * the agent PC's clock skew, but if task-router's clock runs behind
 * gpt-context's, `remaining_ms` can be minutes: the card would stay up and every
 * answer would land after gpt-context's `late_slack_ms`. The AI never holds its
 * reply longer than 6 s.
 */
export const ASSIST_MAX_REMAINING_MS = 6000;
/**
 * A card with less time left than this at receipt is dropped unseen (no cue,
 * no card): the agent could not hear and answer it in time.
 */
export const ASSIST_MIN_TIME_LEFT_MS = 1000;

/** The time a card has from receipt to its local deadline. */
export const assistTimeLeftMs = (remainingMs: number) =>
  Math.min(remainingMs, ASSIST_MAX_REMAINING_MS) - ASSIST_NETWORK_MARGIN_MS;

/** Under `ASSIST_MIN_TIME_LEFT_MS` left: too late to show. */
export const isAssistTooLate = (remainingMs: number) =>
  assistTimeLeftMs(remainingMs) < ASSIST_MIN_TIME_LEFT_MS;

export const isAssistCardActive = (
  card: IAssistCard | null
): card is IAssistCard => card?.status === 'assigned';

export const initialState: IAssistRequestState = {
  card: null,
  endedIds: [],
};

interface IEnvelopeRoute {
  sessionId: string;
  restaurantCode: string;
  laneId?: string;
  /** Local epoch ms. */
  receivedAt: number;
}

const markEnded = (state: IAssistRequestState, requestId: string) => {
  if (state.endedIds.includes(requestId)) return;
  state.endedIds.push(requestId);
  if (state.endedIds.length > MAX_ENDED_IDS) {
    state.endedIds.splice(0, state.endedIds.length - MAX_ENDED_IDS);
  }
};

/**
 * Ends the live card for `requestId`; a card already ended is left alone. Why
 * and when it ended is logged by the hooks (`useAssistPool`,
 * `useAssistAnswer`), not kept here: nothing reads it back (review B1.9).
 */
const endCard = (
  state: IAssistRequestState,
  requestId: string,
  status: Exclude<AssistCardStatus, 'assigned'>
) => {
  const card = state.card;
  if (!isAssistCardActive(card) || card.requestId !== requestId) return;
  card.status = status;
};

const assistRequestSlice = createSlice({
  name: 'assistRequest',
  initialState,
  reducers: {
    assistRequestAssigned(
      state,
      action: PayloadAction<IEnvelopeRoute & { data: IAssistRequestAssigned }>
    ) {
      const { data, receivedAt, sessionId, restaurantCode, laneId } =
        action.payload;
      const requestId = data.request_id;
      if (state.endedIds.includes(requestId)) return;
      // Too late to hear and answer: never shown. `routeAssistPoolEvent`
      // drops (and logs) these first; this keeps the slice honest on its own.
      if (isAssistTooLate(data.remaining_ms)) return;
      // A redelivery of the card we hold is a no-op; any other assignment
      // while a card is live breaks "one card at a time" and is ignored.
      if (isAssistCardActive(state.card)) return;
      state.card = {
        requestId,
        type: data.type,
        sessionId,
        restaurantCode,
        laneId,
        request: data.request,
        receivedAt,
        deadlineAt: receivedAt + assistTimeLeftMs(data.remaining_ms),
        ...(typeof data.assignment_token === 'string' && data.assignment_token
          ? { assignmentToken: data.assignment_token }
          : {}),
        status: 'assigned',
      };
    },

    /** The card's local deadline passed unanswered: it ends, nothing sent. */
    assistCardDeadlinePassed(
      state,
      action: PayloadAction<{ requestId: string; at: number }>
    ) {
      const { requestId } = action.payload;
      endCard(state, requestId, 'expired');
      markEnded(state, requestId);
    },

    /** One click is final: the answer ends the card. */
    assistAnswerSent(
      state,
      action: PayloadAction<{
        requestId: string;
        answerId: string;
        answerChoice: AssistAnswerChoice;
        answerText?: string | null;
        answeredAt: number;
      }>
    ) {
      const card = state.card;
      const { requestId } = action.payload;
      if (!isAssistCardActive(card) || card.requestId !== requestId) return;
      endCard(state, requestId, 'answered');
      markEnded(state, requestId);
    },

    /** The card shell is done showing an ended card. */
    assistCardDismissed(state) {
      if (isAssistCardActive(state.card)) return;
      state.card = null;
    },

    /**
     * An order offer, leaving the lobby or the flag turning off: drop the card
     * at once. Nothing is sent: unanswered, it is simply one answer fewer.
     */
    assistPoolCleared(
      state,
      action: PayloadAction<{ reason: string; at: number }>
    ) {
      const card = state.card;
      if (isAssistCardActive(card)) {
        // A redelivery of the dropped card must not come back with a new cue.
        markEnded(state, card.requestId);
      }
      state.card = null;
    },
  },
});

export const {
  assistRequestAssigned,
  assistCardDeadlinePassed,
  assistAnswerSent,
  assistCardDismissed,
  assistPoolCleared,
} = assistRequestSlice.actions;

export default assistRequestSlice.reducer;
